import { loadStripe } from "@stripe/stripe-js";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaMinus, FaPlus } from "react-icons/fa6";
import { MdShoppingBag } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import CouponInput from "../components/CouponInput";
import {
  CART_TITLE,
  CHECKOUT_TEXT,
  DISCOUNT_LABEL,
  EMPTY_CART_SUBTITLE,
  EMPTY_CART_TITLE,
  KEEP_SHOPPING_TEXT,
  ORDER_SUMMARY_TITLE,
  ROUTES,
  START_SHOPPING_TEXT,
  SUBTOTAL_LABEL,
  TOTAL_LABEL,
} from "../constants";
import Context from "../context";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

// Initialized at module scope — Stripe docs explicitly require this to avoid re-initialization
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Cart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const context = useContext(Context);
  const navigate = useNavigate();
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestDetails, setGuestDetails] = useState({
    email: "",
    name: "",
  });
  const loadingCart = new Array(3).fill(null);
  const isLoggedIn = !!useSelector((state) => state?.user?.user);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async ({ showLoader = true } = {}) => {
      if (showLoader && isMountedRef.current) {
        setLoading(true);
      }

      try {
        if (!isLoggedIn) {
          const localCart = JSON.parse(
            localStorage.getItem("guestCart") || "[]",
          );
          const cartWithDetails = await Promise.all(
            localCart.map(async (item) => {
              try {
                const response = await request(SummaryApi.productDetails, {
                  body: JSON.stringify({ productId: item.productId }),
                });
                const resData = await response.json();
                if (resData.success && resData.data) {
                  return {
                    _id: item.variantId
                      ? `${item.productId}_${item.variantId}`
                      : item.productId,
                    productId: resData.data,
                    variantId: item.variantId,
                    quantity: item.quantity,
                  };
                }
                return null;
              } catch {
                return null;
              }
            }),
          );

          if (isMountedRef.current) {
            setData(cartWithDetails.filter((item) => item !== null));
          }
          return;
        }

        const response = await request(SummaryApi.addToCartProductView);
        const responseData = await response.json();
        if (responseData.success && isMountedRef.current) {
          setData(responseData.data);
        }
      } catch (err) {
        console.error(
          isLoggedIn
            ? "Error fetching cart:"
            : "Error fetching guest cart details:",
          err,
        );
      } finally {
        if (showLoader && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateQuantity = async (
    id,
    newQty,
    variantId = null,
    maxStock = Infinity,
  ) => {
    if (newQty < 1) return;
    if (newQty > maxStock) {
      toast.error(`Only ${maxStock} in stock`);
      return;
    }

    if (!isLoggedIn) {
      let cart = JSON.parse(localStorage.getItem("guestCart") || "[]");
      const index = cart.findIndex(
        (item) =>
          item.productId === id &&
          (item.variantId || null) === (variantId || null),
      );
      if (index !== -1) {
        cart[index].quantity = newQty;
        localStorage.setItem("guestCart", JSON.stringify(cart));
        await fetchData({ showLoader: false });
        await context.fetchUserAddToCart();
      }
      return;
    }

    const response = await request(SummaryApi.updateCartProduct, {
      body: JSON.stringify({ _id: id, quantity: newQty }),
    });
    const responseData = await response.json();
    if (responseData.success) {
      await fetchData({ showLoader: false });
    }
  };

  const deleteCartProduct = async (id, variantId = null) => {
    if (!isLoggedIn) {
      let cart = JSON.parse(localStorage.getItem("guestCart") || "[]");
      cart = cart.filter(
        (item) =>
          !(
            item.productId === id &&
            (item.variantId || null) === (variantId || null)
          ),
      );
      localStorage.setItem("guestCart", JSON.stringify(cart));
      await fetchData({ showLoader: false });
      await context.fetchUserAddToCart();
      return;
    }

    const response = await request(SummaryApi.deleteCartProduct, {
      body: JSON.stringify({ _id: id }),
    });
    const responseData = await response.json();
    if (responseData.success) {
      await fetchData({ showLoader: false });
      await context.fetchUserAddToCart();
    }
  };

  const handlePayment = async () => {
    try {
      const stripeInstance = await stripePromise;
      const response = await request(SummaryApi.payment, {
        body: JSON.stringify({
          cartItems: data,
          couponCode: appliedCoupon?.code,
          guestEmail: guestDetails.email,
          guestName: guestDetails.name,
        }),
      });

      const responseData = await response.json();

      if (responseData.success === false || responseData.error) {
        toast.error(responseData.message || "Checkout failed");
        return;
      }

      const sessionId =
        responseData?.data?.sessionId || responseData?.sessionId;

      if (sessionId) {
        // Clear guest cart if checkout session is successfully created
        // Wait, it should ideally be cleared on success page,
        // but for Stripe Checkout, we can't easily clear it on redirect unless we do it here.
        // However, the user might cancel. So clearing on success page is better.
        stripeInstance.redirectToCheckout({ sessionId });
      } else {
        toast.error("Failed to initiate checkout session");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("An error occurred during checkout. Please try again.");
    }
  };

  const handleCheckoutClick = () => {
    if (!isLoggedIn) {
      setShowGuestForm(true);
    } else {
      handlePayment();
    }
  };

  const handleGuestSubmit = (e) => {
    e.preventDefault();
    if (!guestDetails.email || !guestDetails.name) {
      toast.error("Please provide your email and name");
      return;
    }
    handlePayment();
  };

  const getCartItemPrice = (item) => {
    if (item.variantId && item.productId?.variants) {
      const variant = item.productId.variants.find(
        (v) => v._id === item.variantId,
      );
      if (variant) return variant.sellingPrice;
    }
    return item.productId?.sellingPrice || 0;
  };

  const getCartItemVariantInfo = (item) => {
    if (item.variantId && item.productId?.variants) {
      const variant = item.productId.variants.find(
        (v) => v._id === item.variantId,
      );
      if (variant) {
        const parts = [];
        if (variant.size) parts.push(`Size: ${variant.size}`);
        if (variant.color) parts.push(`Color: ${variant.color}`);
        return parts.join(" | ");
      }
    }
    return null;
  };

  const totalQty = data.reduce((prev, curr) => prev + curr.quantity, 0);
  const subtotal = data.reduce(
    (prev, curr) => prev + curr.quantity * getCartItemPrice(curr),
    0,
  );
  const discount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const totalPrice = subtotal - discount;

  return (
    <div className="bg-surface-muted min-h-screen pb-28 lg:pb-0">
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-10 lg:py-16">
        {}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-primary tracking-tight">
              {CART_TITLE}
            </h1>
            <p className="text-neutral-500 text-sm mt-0.5">
              {totalQty} {totalQty === 1 ? "item" : "items"} in your bag
            </p>
          </div>
          <Link
            to={ROUTES.HOME}
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
          >
            <FaArrowLeft className="text-xs" /> {KEEP_SHOPPING_TEXT}
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 lg:items-start">
          {/* Cart Items */}
          <div className="flex-1 space-y-4 md:space-y-6">
            <AnimatePresence mode="popLayout">
              {loading ? (
                loadingCart.map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-card p-4 md:p-6 border border-surface-border animate-pulse flex gap-4 md:gap-8"
                  >
                    <div className="w-24 h-24 md:w-32 md:h-32 bg-surface-muted rounded-2xl flex-shrink-0" />
                    <div className="flex-1 flex flex-col justify-between py-1 space-y-4">
                      <div className="space-y-2">
                        <div className="h-4 md:h-6 bg-surface-muted rounded-full w-3/4" />
                        <div className="h-3 md:h-4 bg-surface-muted rounded-full w-1/4" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="h-8 md:h-10 bg-surface-muted rounded-full w-24 md:w-32" />
                        <div className="h-6 bg-surface-muted rounded-full w-16 md:w-24" />
                      </div>
                    </div>
                  </div>
                ))
              ) : data.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-card border border-surface-border p-12 md:p-16 text-center shadow-card"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MdShoppingBag className="text-neutral-300 text-3xl" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-black text-primary mb-2">
                    {EMPTY_CART_TITLE}
                  </h2>
                  <p className="text-sm text-neutral-500 mb-8 max-w-xs mx-auto">
                    {EMPTY_CART_SUBTITLE}
                  </p>
                  <button
                    onClick={() => navigate(ROUTES.HOME)}
                    className="px-8 py-3 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all shadow-button-primary active:scale-95"
                  >
                    {START_SHOPPING_TEXT}
                  </button>
                </motion.div>
              ) : (
                data.map((product) => (
                  <motion.div
                    key={product?._id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-card p-4 md:p-6 border border-surface-border shadow-card hover:shadow-card-hover transition-all group"
                  >
                    <div className="flex gap-4 md:gap-8">
                      {/* Product Image */}
                      <div className="w-24 h-24 md:w-32 md:h-32 bg-surface-muted rounded-2xl p-3 md:p-4 flex-shrink-0">
                        <img
                          src={product?.productId?.productImage?.[0]}
                          className="w-full h-full object-contain mix-blend-multiply transition-transform group-hover:scale-105"
                          alt={product?.productId?.productName}
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div>
                          <div className="flex justify-between items-start gap-4">
                            <Link
                              to={`/product/${product?.productId?._id}`}
                              className="flex-1"
                            >
                              <h3 className="text-sm md:text-xl font-black text-primary group-hover:text-accent transition-colors line-clamp-2 md:line-clamp-1">
                                {product?.productId?.productName}
                              </h3>
                            </Link>
                            <span className="hidden md:block text-lg font-black text-primary">
                              {displayNPRCurrency(
                                getCartItemPrice(product) * product.quantity,
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest">
                              {product?.productId?.category}
                            </p>
                            {getCartItemVariantInfo(product) && (
                              <p className="text-[10px] md:text-xs font-black text-accent uppercase tracking-widest border-l border-surface-border pl-3">
                                {getCartItemVariantInfo(product)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center bg-surface-muted rounded-full p-0.5 md:p-1 border border-surface-border">
                            <button
                              onClick={() =>
                                updateQuantity(
                                  isLoggedIn
                                    ? product?._id
                                    : product?.productId?._id,
                                  product?.quantity - 1,
                                  product?.variantId || null,
                                )
                              }
                              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full hover:bg-white transition-all text-primary active:scale-90"
                              aria-label="Decrease Quantity"
                            >
                              <FaMinus className="text-[8px] md:text-[10px]" />
                            </button>
                            <span className="w-8 md:w-12 text-center font-black text-primary text-xs md:text-sm">
                              {product?.quantity}
                            </span>
                            <button
                              onClick={() => {
                                const stockMax = product?.variantId
                                  ? (product?.productId?.variants?.find(
                                      (v) => v._id === product.variantId,
                                    )?.stock ?? Infinity)
                                  : (product?.productId?.stock ?? Infinity);
                                updateQuantity(
                                  isLoggedIn
                                    ? product?._id
                                    : product?.productId?._id,
                                  product?.quantity + 1,
                                  product?.variantId || null,
                                  stockMax,
                                );
                              }}
                              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full hover:bg-white transition-all text-primary active:scale-90"
                              aria-label="Increase Quantity"
                            >
                              <FaPlus className="text-[8px] md:text-[10px]" />
                            </button>
                          </div>

                          <div className="flex items-center gap-4">
                            <span className="md:hidden font-black text-primary text-sm">
                              {displayNPRCurrency(
                                getCartItemPrice(product) * product.quantity,
                              )}
                            </span>
                            <button
                              onClick={() =>
                                deleteCartProduct(
                                  isLoggedIn
                                    ? product?._id
                                    : product?.productId?._id,
                                  product?.variantId || null,
                                )
                              }
                              className="p-2 text-red-400 hover:text-red-500 transition-colors"
                              title="Remove Item"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {/* Checkout Summary */}
          {data.length > 0 && (
            <div className="w-full lg:w-96 lg:sticky lg:top-28 pb-12 lg:pb-0 space-y-4">
              {/* Coupon first */}
              <div>
                <CouponInput
                  cartTotal={subtotal}
                  onCouponApply={(coupon) => setAppliedCoupon(coupon)}
                />
              </div>

              <div className="bg-white border border-surface-border rounded-card shadow-card p-6">
                <h2 className="text-lg font-black text-primary mb-5">
                  {ORDER_SUMMARY_TITLE}
                </h2>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-500 font-medium">
                      {SUBTOTAL_LABEL}
                    </span>
                    <span className="font-bold text-primary">
                      {displayNPRCurrency(subtotal)}
                    </span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center text-green-600">
                      <span className="font-medium">
                        {DISCOUNT_LABEL} ({appliedCoupon.code})
                      </span>
                      <span className="font-bold">
                        -{displayNPRCurrency(discount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-500 font-medium">
                      Shipping
                    </span>
                    <span className="font-bold text-green-600">FREE</span>
                  </div>

                  <div className="pt-4 border-t border-surface-border mt-2 flex justify-between items-center">
                    <span className="font-black text-primary">
                      {TOTAL_LABEL}
                    </span>
                    <span className="text-2xl font-black text-primary tracking-tight">
                      {displayNPRCurrency(totalPrice)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckoutClick}
                  className="w-full h-12 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all duration-300 shadow-button-primary active:scale-[0.98] mt-5"
                >
                  {CHECKOUT_TEXT}
                </button>

                <div className="flex items-center justify-center gap-2 mt-4 text-neutral-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                  <p className="text-2xs font-semibold uppercase tracking-wider">
                    Secure Checkout · Stripe
                  </p>
                </div>
              </div>

              {/* Guest Checkout form */}
              <AnimatePresence>
                {showGuestForm && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="bg-white border border-surface-border rounded-card p-6 shadow-card"
                  >
                    <h3 className="text-base font-black text-primary mb-1">
                      Quick Checkout
                    </h3>
                    <p className="text-xs text-neutral-500 mb-4">
                      Enter your details to continue as a guest.
                    </p>
                    <form onSubmit={handleGuestSubmit} className="space-y-3">
                      <div>
                        <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1">
                          Full Name
                        </label>
                        <input
                          required
                          type="text"
                          className="input-base"
                          placeholder="John Doe"
                          value={guestDetails.name}
                          onChange={(e) =>
                            setGuestDetails((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1">
                          Email Address
                        </label>
                        <input
                          required
                          type="email"
                          className="input-base"
                          placeholder="john@example.com"
                          value={guestDetails.email}
                          onChange={(e) =>
                            setGuestDetails((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-2 pt-1">
                        <button
                          type="submit"
                          className="w-full h-11 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all shadow-button-primary"
                        >
                          Continue to Payment
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowGuestForm(false)}
                          className="text-xs font-semibold text-neutral-500 hover:text-primary transition-colors"
                        >
                          Back to Summary
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
